import { useQuery } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { getRequest, serialize } from 'src/utils/request';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';

import type { RequestDataArray } from 'src/types/requestData';

const PARAMS = {
  checkIn: { check_type: 'check_in' },
  checkOut: { check_type: 'check_out' },
} as const;

const mapDeviationReasonOptions = (
  option: UCM.DeviationReasonOptionRawType,
): UCM.DeviationReasonOptionType => ({
  value: option.code,
  label: option.title,
  reason: option.reason_for,
});

const getDeviationReasonOptions = async (key: keyof typeof PARAMS) => {
  const queryParams = serialize(PARAMS[key]);

  const { data } = await getRequest<
    RequestDataArray<UCM.DeviationReasonOptionRawType>
  >(`${API.GET.deviationReasonOptions}?${queryParams}`);
  return data.data.map(({ attributes }) =>
    mapDeviationReasonOptions(attributes),
  );
};

export const useGetDeviationReasonOptions = (
  config: { enabled?: boolean } = { enabled: true },
) => {
  const checkInQuery = useQuery({
    queryKey: [QUERY_KEYS.DEVIATION_REASON_OPTIONS, 'checkIn'],
    queryFn: () => getDeviationReasonOptions('checkIn'),
    staleTime: 1000 * 60 * 10,
    ...config,
  });

  const checkOutQuery = useQuery({
    queryKey: [QUERY_KEYS.DEVIATION_REASON_OPTIONS, 'checkOut'],
    queryFn: () => getDeviationReasonOptions('checkOut'),
    staleTime: 1000 * 60 * 10,
    ...config,
  });

  return {
    data: {
      checkIn: checkInQuery.data,
      checkOut: checkOutQuery.data,
    },
    isLoading: {
      checkIn: checkInQuery.isLoading,
      checkOut: checkOutQuery.isLoading,
    },
    isError: {
      checkIn: checkInQuery.isError,
      checkOut: checkOutQuery.isError,
    },
    error: {
      checkIn: checkInQuery.error,
      checkOut: checkOutQuery.error,
    },
  };
};
